import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import HistoryIcon from "@mui/icons-material/History";
import UndoIcon from "@mui/icons-material/Undo";
import type React from "react";
import { type ReactElement, useEffect, useState } from "react";
import { forwardRef } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { HoverCardClickable } from "./HoverCardClickable";

export interface AIFieldStage {
  timestamp: number;
  value: any;
  preValue: any;
}

/**
 * A proposed value the user never acted on (expired conflict toast, deferred voice/AI
 * suggestion). Rendered as an amber "Last Ignored Update" entry; clicking it opens the
 * Compare/merge modal via onCompare (falls back to inserting the value when no modal
 * integration is available).
 */
export interface IgnoredUpdateInfo {
  value: string;
  onCompare?: () => void;
}

interface AlternateAIFieldValueIconProps extends React.HTMLAttributes<HTMLDivElement> {
  alternateValues: string[];
  alternateValuesLabels: string[];
  textState?: string | string[] | Record<string, any>;
  triggerJSX?: ReactElement | null;
  side?: "top" | "bottom" | "left" | "right";
  triggerClassName?: string;
  className?: string;
  sideOffset?: number;
  handleAlternativeSelect?: (value: string) => void;
  stages?: AIFieldStage[];
  lastSavedValue?: string;
  ignoredUpdate?: IgnoredUpdateInfo;
  /**
   * Controlled undo slot: when onCaptureUndo is provided the component stops using its own
   * local snapshot — selections report the pre-selection value through onCaptureUndo and the
   * Undo button renders from undoValue. Lets writes made OUTSIDE the popover (e.g. conflict
   * resolves) share the same undo slot. "" is a valid snapshot (the field was blank); only
   * null/undefined mean "nothing captured".
   */
  undoValue?: string | null;
  onCaptureUndo?: (value: string) => void;
  /** Fires each time the popover content opens (mount-based — covers hover and click opens). */
  onPopoverOpened?: () => void;
}

/** Radix only mounts HoverCard content while open, so mounting this inside the content is
 * a reliable "the popover opened" signal (used to clear the undo-attention nudge). */
function PopoverOpenedSignal({ onOpened }: { onOpened: () => void }) {
  useEffect(() => {
    onOpened();
  }, [onOpened]);
  return null;
}

export const AlternateAIFieldValueIcon = forwardRef<HTMLDivElement, AlternateAIFieldValueIconProps>(
  (
    {
      textState,
      side = "top",
      sideOffset = -12,
      className,
      triggerClassName,
      alternateValues,
      alternateValuesLabels,
      triggerJSX,
      handleAlternativeSelect,
      stages,
      lastSavedValue,
      ignoredUpdate,
      undoValue,
      onCaptureUndo,
      onPopoverOpened,
      ...props
    },
    ref
  ) => {
    const [showHistory, setShowHistory] = useState(false);
    // Value captured just before the last popover selection wrote to the field — lets the
    // user undo an accidental click. Capturing again on undo makes it a redo toggle.
    // Used only when the slot is not lifted via onCaptureUndo/undoValue.
    const [localUndoValue, setLocalUndoValue] = useState<{ value: string } | null>(null);
    const captureUndo = onCaptureUndo ?? ((value: string) => setLocalUndoValue({ value }));
    const effectiveUndo = onCaptureUndo ? (undoValue != null ? { value: undoValue } : null) : localUndoValue;
    const hasHistory = stages && stages.length > 1;

    const applySelection = (value: any) => {
      if (typeof textState === "string") {
        captureUndo(textState);
      } else if (textState === undefined || textState === null) {
        captureUndo("");
      }
      // Non-string states (arrays/objects) are not snapshotted — keep the previous capture.
      handleAlternativeSelect?.(value);
    };

    // Build the display values: original 3 + Last Save (if different from others)
    const displayValues = [...alternateValues];
    const displayLabels = [...alternateValuesLabels];
    if (lastSavedValue !== undefined && lastSavedValue !== null) {
      displayValues.push(lastSavedValue);
      displayLabels.push("Last Save");
    }

    const isValueDifferent = (value: any) => {
      if (typeof textState === "string") return textState !== value;
      if (textState && !Array.isArray(textState) && typeof textState === "object") return value !== (textState as Record<string, any>).value;
      if (Array.isArray(textState))
        return !textState.some((v) => {
          if (v && typeof v === "object") return v.hasOwnProperty("value") ? (v as Record<string, any>).value !== value : false;
          if (typeof v === "string") return v !== value;
        });
      return true;
    };

    const formatTime = (ts: number) => {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    };

    const truncate = (val: any, max = 40) => {
      const s = String(val ?? "");
      return s.length > max ? s.slice(0, max) + "..." : s;
    };

    return (
      <HoverCardClickable
        side={side}
        sideOffset={sideOffset}
        className={cn("px-1", className)}
        triggerJSX={
          triggerJSX ?? (
            <AutoAwesomeIcon
              sx={{ fontSize: 14 }}
              data-testid="alternate-values-trigger"
              className={cn(triggerClassName, { "!text-amber-500": !!ignoredUpdate })}
            />
          )
        }
        triggerClassName={triggerClassName}
      >
        <div className={cn("flex flex-col gap-y-1", { "min-w-[280px]": showHistory })}>
          {onPopoverOpened && <PopoverOpenedSignal onOpened={onPopoverOpened} />}
          {ignoredUpdate && (
            <button
              type="button"
              data-testid="ignored-update-compare"
              title="Open a side-by-side compare of this ignored update against the current value"
              className="mb-0.5 flex w-full max-w-[280px] flex-col items-start gap-y-0.5 rounded-md border border-amber-300 bg-amber-50 p-1.5 text-left hover:border-amber-400 hover:bg-amber-100"
              onClick={() => {
                if (ignoredUpdate.onCompare) ignoredUpdate.onCompare();
                else applySelection(ignoredUpdate.value);
              }}
            >
              <span className="flex items-center gap-x-1 text-xs font-medium text-amber-700">
                <CompareArrowsIcon sx={{ fontSize: 14 }} />
                Last Ignored Update — click to compare
              </span>
              <span className="w-full truncate text-xs text-secondary-dark">{truncate(ignoredUpdate.value, 80)}</span>
            </button>
          )}
          <p className="text-sm">Change the field value</p>
          <div className="flex flex-wrap gap-x-2">
            {(() => {
              const rendered = new Set<any>();
              return displayValues.map((value, index) => {
                if (value === undefined || value === null || rendered.has(value)) return null;
                rendered.add(value);
                const isDifferent = isValueDifferent(value);
                return (
                  <Button
                    key={"changeField" + index}
                    variant="link"
                    size={"tight"}
                    className={cn("px-0 py-0 pb-1 text-secondary-dark", {
                      "hover:text-purple": isDifferent,
                      "cursor-default text-darkPurple underline": !isDifferent,
                    })}
                    onClick={() => {
                      if (!isDifferent) return;
                      applySelection(value);
                    }}
                  >
                    <span>{displayLabels[index]}</span>
                  </Button>
                );
              });
            })()}
            {effectiveUndo !== null && isValueDifferent(effectiveUndo.value) && (
              <Button
                variant="link"
                size={"tight"}
                data-testid="alternate-values-undo"
                title="Restore the value from before the last change made here or by a conflict resolution"
                className="px-0 py-0 pb-1 text-amber-600 hover:text-purple"
                onClick={() => applySelection(effectiveUndo.value)}
              >
                <UndoIcon sx={{ fontSize: 14 }} />
                <span className="ml-0.5">Undo</span>
              </Button>
            )}
            {hasHistory && (
              <Button
                variant="link"
                size={"tight"}
                className="px-0 py-0 pb-1 text-tertiary-dark hover:text-purple"
                onClick={() => setShowHistory(!showHistory)}
              >
                <HistoryIcon sx={{ fontSize: 14 }} />
                <span className="ml-0.5">{showHistory ? "Hide" : "History"}</span>
              </Button>
            )}
          </div>
          {showHistory && hasHistory && (
            <div className="mt-1 max-h-[200px] overflow-y-auto border-t border-gray-200 pt-1">
              <p className="mb-1 text-xs text-tertiary-dark">Change history (newest first)</p>
              {[...stages!].reverse().map((stage, i) => (
                <div key={stage.timestamp + "-" + i} className="group flex items-start gap-x-2 rounded px-1 py-0.5 text-xs hover:bg-gray-50">
                  <span className="shrink-0 text-tertiary-dark">{formatTime(stage.timestamp)}</span>
                  <span className="shrink-0 text-tertiary-dark">&rarr;</span>
                  <button
                    type="button"
                    className="truncate text-left text-secondary-dark hover:text-purple hover:underline"
                    title={String(stage.value ?? "")}
                    onClick={() => applySelection(stage.value)}
                  >
                    {truncate(stage.value)}
                  </button>
                  <span className="ml-auto shrink-0 text-tertiary-dark opacity-0 group-hover:opacity-100">was: {truncate(stage.preValue, 20)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </HoverCardClickable>
    );
  }
);

AlternateAIFieldValueIcon.displayName = "AlternateAIFieldValueIcon";
